import React, { useState, useEffect } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog'
import { Button } from '../../ui/button'
import { Input } from '../../ui/input'
import { Label } from '../../ui/label'
import { Textarea } from '../../ui/textarea'
import { Loader2 } from 'lucide-react'
import ReportEndpoints from '../../../endpoints/ReportEndpoints'
import { toast } from 'sonner'

interface ScheduleEmailDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  content: string
  signature: string
  clientId: number | null
  defaultSubject?: string
  defaultRecipients?: string
}

export const ScheduleEmailDialog: React.FC<ScheduleEmailDialogProps> = ({
  open,
  onOpenChange,
  content,
  signature,
  clientId,
  defaultSubject,
  defaultRecipients,
}) => {
  const [recipients, setRecipients] = useState('')
  const [subject, setSubject] = useState('')
  const [customMessage, setCustomMessage] = useState('')
  const [sending, setSending] = useState(false)

  useEffect(() => {
    if (open) {
      setRecipients(defaultRecipients || '')
      setSubject(defaultSubject || 'Schedule Export')
      setCustomMessage('')
    }
  }, [open, defaultSubject, defaultRecipients])

  const handleSend = async () => {
    const recipientList = recipients
      .split(',')
      .map((s) => s.trim())
      .filter((s) => s.length > 0)

    if (recipientList.length === 0) {
      toast.error('Enter at least one recipient email')
      return
    }
    if (!subject.trim()) {
      toast.error('Subject is required')
      return
    }

    setSending(true)
    try {
      await ReportEndpoints.sendReportEmail({
        content,
        signature,
        customMessage,
        subject,
        recipients: recipientList,
        clientId,
      })
      toast.success('Email sent')
      onOpenChange(false)
    } catch (err: any) {
      const msg = err?.message || (typeof err === 'string' ? err : 'Failed to send email')
      toast.error(msg)
    } finally {
      setSending(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !sending && onOpenChange(o)}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Email Schedule</DialogTitle>
          <DialogDescription>
            Send the generated schedule report to one or more recipients.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="schedule-email-recipients" className="text-sm font-medium">
              Recipients
            </Label>
            <Input
              id="schedule-email-recipients"
              type="text"
              value={recipients}
              onChange={(e) => setRecipients(e.target.value)}
              placeholder="comma-separated emails"
              disabled={sending}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="schedule-email-subject" className="text-sm font-medium">
              Subject
            </Label>
            <Input
              id="schedule-email-subject"
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              disabled={sending}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="schedule-email-message" className="text-sm font-medium">
              Message (Optional)
            </Label>
            <Textarea
              id="schedule-email-message"
              value={customMessage}
              onChange={(e) => setCustomMessage(e.target.value)}
              rows={4}
              disabled={sending}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={sending}>
            Cancel
          </Button>
          <Button onClick={handleSend} disabled={sending}>
            {sending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Sending...
              </>
            ) : (
              'Send Email'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
